import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, message, Space, Tag, Card, Descriptions, Divider, Tabs, Typography, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { productService } from '../../services/productService';
import cdnService from '../../services/cdnService';
import sslService from '../../services/sslService';
import dnsService from '../../services/dnsService';
import { adminApi } from '../../services/api';
import currencyService from '../../services/currencyService';
import ProductFeaturesEditor from '../../components/ProductFeaturesEditor';
import ProductGiftRules from '../../components/billing/ProductGiftRules';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';
import ProductTypeNames from './ProductTypeNames';
import CdnPlanGroupsEditor from '../../components/cdn/CdnPlanGroupsEditor';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const Products = () => {
    const [products, setProducts] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [cdnGroups, setCdnGroups] = useState([]);
    const [planUsage, setPlanUsage] = useState([]);
    const [dnsParentGroups, setDnsParentGroups] = useState([]);
    const [mailClusters, setMailClusters] = useState([]);
    const [sslProviders, setSslProviders] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [useVisualEditor, setUseVisualEditor] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        loadProductTypes();
        loadProducts();
        loadCdnGroups();
        loadSslProviders();
        loadDnsParentGroups();
        loadMailClusters();
        loadCurrencies();
    }, []);

    useEffect(() => {
        loadProducts();
    }, [activeTab]);

    const loadProductTypes = async () => {
        try {
            const response = await productService.getProductTypes();
            if (response.success) {
                setProductTypes(response.data);
            }
        } catch (error) {
            message.error('加载产品类型失败：' + error.message);
        }
    };

    const loadDnsParentGroups = async () => {
        try {
            const response = await dnsService.getNodeParentGroups();
            if (response.success) {
                setDnsParentGroups(response.data || []);
            }
        } catch (error) {
            console.error('加载DNS父节点组失败:', error);
        }
    };

    const loadMailClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            const list = res?.data?.data ?? res?.data ?? [];
            setMailClusters(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error('加载邮件集群失败:', error);
        }
    };

    const loadCdnGroups = async () => {
        try {
            const response = await cdnService.getCdnGroups();
            if (response.success) {
                setCdnGroups(response.data || []);
            }
        } catch (error) {
            console.error('加载CDN分组失败:', error);
        }
    };

    const loadSslProviders = async () => {
        try {
            const response = await sslService.getProviders(true);
            if (response.success) {
                setSslProviders(response.data || []);
            }
        } catch (error) {
            console.error('加载SSL提供商失败:', error);
        }
    };

    const loadCurrencies = async () => {
        try {
            const response = await currencyService.getEnabledCurrencies();
            if (response.success) {
                setCurrencies(response.data || []);
            }
        } catch (error) {
            console.error('加载货币失败:', error);
        }
    };

    const loadProducts = async () => {
        setLoading(true);
        try {
            const response = await productService.getProducts();
            if (response.success) {
                let filteredProducts = response.data;

                // 根据 activeTab 筛选产品
                if (activeTab !== 'all') {
                    filteredProducts = filteredProducts.filter(p => p.product_type_code === activeTab);
                }

                setProducts(filteredProducts);
            } else {
                message.error(response.error || '加载产品列表失败');
            }
        } catch (error) {
            message.error('加载失败：' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingProduct(null);
        setSelectedProductType(null);
        setPlanUsage([]);
        form.resetFields();
        setModalVisible(true);
    };

    // CDN 套餐：有序分组 + 上限 + 溢出组
    const loadPlanGroups = async (record) => {
        const fallback = record.cdn_group_id ? { entries: [{ group_id: record.cdn_group_id, domain_cap: null }], overflow_group_id: record.cdn_group_id } : { entries: [] };
        form.setFieldsValue({ cdn_plan: fallback });
        const r = await cdnService.getPlanGroups(record.id);
        if (r?.success) {
            const rows = r.data.entries || [];
            setPlanUsage(rows);
            form.setFieldsValue({ cdn_plan: {
                entries: rows.map((e) => ({ group_id: e.group_id, domain_cap: e.domain_cap })),
                overflow_group_id: rows.find((e) => e.overflow)?.group_id || rows[0]?.group_id || null,
            } });
        }
    };

    const parseJsonField = (value) => {
        if (!value) return {};
        if (typeof value === 'object') return value;
        try {
            return JSON.parse(value);
        } catch (e) {
            return {};
        }
    };

    const handleEdit = (record) => {
        setEditingProduct(record);
        setSelectedProductType(record.product_type_code);

        // 先重置表单，清除旧值（避免 destroyOnClose 后残留状态）
        form.resetFields();

        // 解析可能为 JSON 字符串的嵌套对象
        const subscriptionLimits = parseJsonField(record.subscription_limits);
        const resourceFeatures = parseJsonField(record.resource_features);
        const periodicUsage = parseJsonField(record.periodic_usage);

        // 将嵌套的 features 对象转换为 form 字段
        const formValues = {
            ...record,
            subscription_limits: subscriptionLimits,
            resource_features: resourceFeatures,
            periodic_usage: periodicUsage,
            features: parseJsonField(record.features),
            dns_parent_group_ids: record.dns_parent_group_ids || [],
            webmail_cluster_ids: Array.isArray(record.webmail_cluster_ids) ? record.webmail_cluster_ids : [],
            i18n: parseI18n(record.i18n),
        };

        // SSL产品：从metadata中提取字段
        if (record.product_type_code === 'ssl' && record.metadata) {
            const metadata = typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata;
            formValues.provider = metadata.provider;
            formValues.product_code = metadata.product_code;
            formValues.brand = metadata.brand;
            formValues.type = metadata.type;
            formValues.validity_period_days = metadata.validity_period_days;
            formValues.max_domains = metadata.max_domains;
            formValues.supports_wildcard = metadata.supports_wildcard;
            formValues.supports_multi_domain = metadata.supports_multi_domain;
            formValues.validation_type = metadata.validation_type;
        }

        // 处理 pricing（获取pricing记录）
        if (record.pricing && record.pricing.length > 0) {
            // 转换pricing数组为form格式
            formValues.pricing = record.pricing.map(p => ({
                billing_cycle: p.billing_cycle,
                price: p.price,
                setup_fee: p.setup_fee || 0,
                currency: p.currency || 'USD',
                enabled: p.enabled !== false
            }));
            // SSL产品兼容
            formValues.sale_price = record.pricing[0].price;
            formValues.currency = record.pricing[0].currency;
        } else if (record.sale_price) {
            // 兼容旧格式
            formValues.sale_price = record.sale_price;
            formValues.currency = record.currency;
        }

        // is_active 字段映射
        formValues.is_active = record.status === 'active';

        form.setFieldsValue(formValues);
        setPlanUsage([]);
        if (record.product_type_code === 'cdn') {
            loadPlanGroups(record);
        }
        setModalVisible(true);
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await productService.getProduct(record.id);
            if (response.success) {
                setSelectedProduct(response.data);
                setDetailModalVisible(true);
            } else {
                message.error(response.error || '加载产品详情失败');
            }
        } catch (error) {
            message.error('加载失败：' + error.message);
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除产品 "${record.name}" 吗？`,
            onOk: async () => {
                try {
                    const response = await productService.deleteProduct(record.id);
                    if (response.success) {
                        message.success('删除成功');
                        loadProducts();
                    } else {
                        message.error(response.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败：' + error.message);
                }
            },
        });
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            message.error('请填写所有必填项');
            return;
        }
        setSubmitting(true);
        try {

            // account_features 和 resource_features 已经是对象格式
            // Form 的嵌套字段会自动组织成对象
            // keep stored translations this form does not show (e.g. features)
            let data = { ...values, i18n: cleanI18n(mergeI18n(editingProduct?.i18n, values.i18n)) };

            // yeapmail 的配额在 products.features；表单只注册了部分键，需与原有 features 合并，避免覆盖丢键
            if (values.features && typeof values.features === 'object') {
                const orig = parseJsonField(editingProduct?.features) || {};
                data.features = { ...(Array.isArray(orig) ? {} : orig), ...values.features };
            }

            // periodic_usage 同理：表单只注册部分子键，逐指标深合并，避免保存时抹掉未展示的配置
            if (values.periodic_usage && typeof values.periodic_usage === 'object') {
                const origPU = parseJsonField(editingProduct?.periodic_usage) || {};
                const merged = { ...(Array.isArray(origPU) ? {} : origPU) };
                Object.entries(values.periodic_usage).forEach(([k, v]) => {
                    merged[k] = { ...(merged[k] || {}), ...(v || {}) };
                    if (v?.overage) merged[k].overage = { ...(origPU?.[k]?.overage || {}), ...v.overage };
                });
                data.periodic_usage = merged;
            }

            // SSL产品特殊处理：构建metadata
            if (values.product_type_code === 'ssl') {
                data.metadata = {
                    provider: values.provider,
                    product_code: values.product_code,
                    brand: values.brand,
                    type: values.type,
                    validity_period_days: values.validity_period_days,
                    max_domains: values.max_domains || 1,
                    supports_wildcard: values.supports_wildcard || false,
                    supports_multi_domain: values.supports_multi_domain || false,
                    validation_type: values.validation_type || values.type,
                };

                // 移除已包含在metadata中的字段
                delete data.provider;
                delete data.product_code;
                delete data.brand;
                delete data.type;
                delete data.validity_period_days;
                delete data.max_domains;
                delete data.supports_wildcard;
                delete data.supports_multi_domain;
                delete data.validation_type;
            }

            if (editingProduct) {
                data.id = editingProduct.id;
            }

            const cdnPlan = data.cdn_plan;
            delete data.cdn_plan;
            if (values.product_type_code === 'cdn' && cdnPlan) {
                data.cdn_group_id = cdnPlan.entries?.[0]?.group_id || null;
            }

            const response = editingProduct
                ? await productService.updateProduct(data)
                : await productService.createProduct(data);

            if (response.success && values.product_type_code === 'cdn' && cdnPlan) {
                const pid = editingProduct?.id || response.product_id || response.data?.id;
                const pr = pid ? await cdnService.savePlanGroups({
                    product_id: pid,
                    groups: (cdnPlan.entries || []).map((e) => ({ group_id: e.group_id, domain_cap: e.domain_cap || 0 })),
                    overflow_group_id: cdnPlan.overflow_group_id,
                }) : null;
                if (pr && !pr.success) {
                    message.warning('分组列表未保存：' + (pr.error || '失败'));
                }
            }

            if (response.success) {
                message.success(editingProduct ? '更新成功' : '创建成功');
                setModalVisible(false);
                loadProducts();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败：' + (error.message || '未知错误'));
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '产品名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: '产品类型',
            dataIndex: 'product_type_code',
            key: 'product_type_code',
            width: 100,
            render: (code) => <Tag color="blue">{code?.toUpperCase()}</Tag>
        },
        {
            title: '计费类型',
            dataIndex: 'billing_type',
            key: 'billing_type',
            width: 100,
            render: (type) => {
                const typeMap = {
                    recurring: '周期性',
                    onetime: '一次性',
                    metered: '按量计费'
                };
                return <Tag color="purple">{typeMap[type] || type}</Tag>;
            }
        },
        {
            title: '显示顺序',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 100,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 'active' ? 'success' : 'default'}>
                    {status === 'active' ? '启用' : status === 'inactive' ? '禁用' : '隐藏'}
                </Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        详情
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card
                title="产品管理"
                extra={
                    <Space>
                        <ProductTypeNames types={productTypes} onSaved={loadProductTypes} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            新建产品
                        </Button>
                    </Space>
                }
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
                    <TabPane tab="全部产品" key="all" />
                    <TabPane tab="DNS 产品" key="dns" />
                    <TabPane tab="CDN 产品" key="cdn" />
                    <TabPane tab="SSL 产品" key="ssl" />
                    <TabPane tab="域名产品" key="domain" />
                    <TabPane tab="共享主机" key="host_shared" />
                    <TabPane tab="TM 邮件" key="tm" />
                    <TabPane tab="域名邮箱" key="yeapmail" />
                    <TabPane tab="云盘" key="disk" />
                </Tabs>

                <Table
                    columns={columns}
                    dataSource={products}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                />
            </Card>

            {/* 创建/编辑模态框 */}
            <Modal
                title={editingProduct ? '编辑产品' : '新建产品'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                confirmLoading={submitting}
                width={1000}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="product_type_code"
                        label="产品类型"
                        rules={[{ required: true, message: '请选择产品类型' }]}
                    >
                        <Select
                            placeholder="选择产品类型"
                            onChange={(value) => setSelectedProductType(value)}
                        >
                            {productTypes.map((type) => (
                                <Option key={type.code} value={type.code}>
                                    {type.name} ({type.code})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedProductType === 'cdn' && (
                        <>
                            <Form.Item
                                name="cdn_plan"
                                label="CDN分组（按顺序分配）"
                                tooltip="新域名进第一个有余量的分组；全满进溢出组"
                                rules={[{ validator: (_, v) => (v?.entries?.length ? Promise.resolve() : Promise.reject(new Error('至少选一个CDN分组'))) }]}
                            >
                                <CdnPlanGroupsEditor groups={cdnGroups} usage={planUsage} />
                            </Form.Item>
                        </>
                    )}

                    {selectedProductType === 'dns' && (
                        <Form.Item
                            name="dns_parent_group_ids"
                            label="DNS父节点组"
                            extra="为 DNS/NS 服务选择一个或多个父节点组，系统会在创建域名时按子分组负载均衡分配 NS"
                            rules={[{ required: true, message: '请选择DNS父节点组' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="选择DNS父节点组"
                                allowClear
                            >
                                {dnsParentGroups.map((group) => (
                                    <Option key={group.id} value={group.id}>
                                        {group.name} (ID: {group.id})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {selectedProductType === 'yeapmail' && !form.getFieldValue('is_addon') && (
                        <Form.Item
                            name="webmail_cluster_ids"
                            label="邮件集群"
                            tooltip="新开通只分到这些集群；不选 = 任意接新域名的集群"
                        >
                            <Select mode="multiple" placeholder="任意集群" allowClear>
                                {mailClusters.map((c) => (
                                    <Option key={c.id} value={c.id}>
                                        {c.name}{c.region && c.region !== 'default' ? ` · ${c.region}` : ''}{c.accept_new === false ? '（不接新域名）' : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {/* SSL产品特有字段 */}
                    {selectedProductType === 'ssl' && (
                        <>
                            <Form.Item
                                name="provider"
                                label="SSL提供商"
                                rules={[{ required: true, message: '请选择SSL提供商' }]}
                            >
                                <Select placeholder="选择SSL提供商">
                                    {sslProviders.map((provider) => (
                                        <Option key={provider.provider} value={provider.provider}>
                                            {provider.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="product_code"
                                label="产品代码"
                                rules={[{ required: true, message: '请输入产品代码' }]}
                            >
                                <Input placeholder="例如: gogetssl-dv-basic" />
                            </Form.Item>

                            <Form.Item
                                name="brand"
                                label="品牌"
                                rules={[{ required: true, message: '请输入品牌' }]}
                            >
                                <Input placeholder="例如: Sectigo, DigiCert" />
                            </Form.Item>

                            <Form.Item
                                name="type"
                                label="证书类型"
                                rules={[{ required: true, message: '请选择证书类型' }]}
                            >
                                <Select placeholder="选择证书类型">
                                    <Option value="DV">DV - 域名验证</Option>
                                    <Option value="OV">OV - 组织验证</Option>
                                    <Option value="EV">EV - 扩展验证</Option>
                                    <Option value="Wildcard">Wildcard - 通配符</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="validity_period_days"
                                label="有效期（天）"
                                rules={[{ required: true, message: '请输入有效期' }]}
                                initialValue={365}
                            >
                                <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name="sale_price"
                                label="销售价格"
                                rules={[{ required: true, message: '请输入价格' }]}
                            >
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="年费价格" />
                            </Form.Item>

                            <Form.Item
                                name="currency"
                                label="货币"
                                initialValue="USD"
                                rules={[{ required: true, message: '请选择货币' }]}
                            >
                                <Select>
                                    {currencies.length > 0 ? currencies.map(c => (
                                        <Option key={c.code} value={c.code}>{c.symbol} {c.code}</Option>
                                    )) : (
                                        <>
                                            <Option value="USD">$ USD</Option>
                                            <Option value="CNY">¥ CNY</Option>
                                            <Option value="EUR">€ EUR</Option>
                                        </>
                                    )}
                                </Select>
                            </Form.Item>
                        </>
                    )}

                    <I18nFieldTabs
                        fields={[{ key: 'name', label: '产品名称' }, { key: 'description', label: '产品描述', type: 'textarea' }]}
                        base={<>
                            <Form.Item
                                name="name"
                                label="产品名称"
                                rules={[{ required: true, message: '请输入产品名称' }]}
                            >
                                <Input placeholder="例如：基础版、专业版" />
                            </Form.Item>

                            <Form.Item name="description" label="产品描述">
                                <TextArea rows={3} placeholder="产品描述信息" />
                            </Form.Item>
                        </>}
                    />

                    <Space wrap style={{ width: '100%' }}>
                        <Form.Item name="is_addon" label="附加资源包" valuePropName="checked" tooltip="作为某产品线套餐的附加包出售（挂在主订阅下）" style={{ marginBottom: 12 }}>
                            <Switch />
                        </Form.Item>
                        <Form.Item name="requires_product_id" label="仅限套餐 ID" tooltip="留空 = 同产品线所有套餐可买" style={{ marginBottom: 12 }}>
                            <InputNumber min={1} style={{ width: 140 }} placeholder="不限" />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="billing_type"
                        label="计费类型"
                        rules={[{ required: true, message: '请选择计费类型' }]}
                        initialValue="recurring"
                    >
                        <Select placeholder="选择计费类型">
                            <Option value="recurring">周期性计费</Option>
                            <Option value="onetime">一次性付费</Option>
                            <Option value="metered">按量计费</Option>
                        </Select>
                    </Form.Item>

                    {/* 非SSL产品的定价配置 */}
                    {selectedProductType && selectedProductType !== 'ssl' && (
                        <>
                            <Divider>定价配置</Divider>
                            <Form.List name="pricing" initialValue={[{ billing_cycle: 'monthly', price: 0, currency: 'USD', enabled: true }]}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card key={key} size="small" style={{ marginBottom: 8 }}>
                                                <Space align="start" style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'billing_cycle']}
                                                        label="计费周期"
                                                        rules={[{ required: true, message: '请选择计费周期' }]}
                                                    >
                                                        <Select style={{ width: 120 }}>
                                                            <Option value="monthly">月付</Option>
                                                            <Option value="quarterly">季付</Option>
                                                            <Option value="semi-annually">半年付</Option>
                                                            <Option value="annually">年付</Option>
                                                            <Option value="biennially">两年付</Option>
                                                            <Option value="triennially">三年付</Option>
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'price']}
                                                        label="价格"
                                                        rules={[{ required: true, message: '请输入价格' }]}
                                                    >
                                                        <InputNumber min={0} step={0.01} style={{ width: 120 }} />
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'setup_fee']}
                                                        label="开通费"
                                                        initialValue={0}
                                                    >
                                                        <InputNumber min={0} step={0.01} style={{ width: 100 }} />
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'currency']}
                                                        label="货币"
                                                        initialValue="USD"
                                                    >
                                                        <Select style={{ width: 100 }}>
                                                            {currencies.length > 0 ? currencies.map(c => (
                                                                <Option key={c.code} value={c.code}>{c.symbol} {c.code}</Option>
                                                            )) : (
                                                                <>
                                                                    <Option value="USD">$ USD</Option>
                                                                    <Option value="CNY">¥ CNY</Option>
                                                                    <Option value="EUR">€ EUR</Option>
                                                                </>
                                                            )}
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'enabled']}
                                                        label="启用"
                                                        valuePropName="checked"
                                                        initialValue={true}
                                                    >
                                                        <Switch size="small" />
                                                    </Form.Item>
                                                    {fields.length > 1 && (
                                                        <Button type="link" danger onClick={() => remove(name)}>
                                                            删除
                                                        </Button>
                                                    )}
                                                </Space>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add({ billing_cycle: 'annually', price: 0, currency: 'USD', enabled: true })} block>
                                            + 添加定价周期
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        </>
                    )}

                    <Form.Item
                        name="display_order"
                        label="显示顺序"
                        rules={[{ required: true, message: '请输入显示顺序' }]}
                        initialValue={100}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="数值越小越靠前" />
                    </Form.Item>

                    {/* SSL产品不需要 features 配置 */}
                    {selectedProductType !== 'ssl' && (
                        <>
                            <Divider>产品特性配置</Divider>
                            {selectedProductType ? (
                                <ProductFeaturesEditor
                                    productTypeCode={selectedProductType}
                                    form={form}
                                />
                            ) : (
                                <Card>
                                    <div style={{ textAlign: 'center', color: '#999' }}>
                                        请先选择产品类型以配置特性
                                    </div>
                                </Card>
                            )}
                        </>
                    )}

                    <Divider />

                    <Form.Item name="is_active" label="是否启用" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Form>
                {editingProduct?.id && selectedProductType === 'yeapmail' && (
                    <>
                        <Divider><Tooltip title="订阅生效即送，随主套餐免费续；换套餐时赠品跟着换">赠品</Tooltip></Divider>
                        <ProductGiftRules productId={editingProduct.id} />
                    </>
                )}
            </Modal>

            {/* 详情模态框 */}
            <Modal
                title="产品详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        关闭
                    </Button>,
                ]}
                width={800}
            >
                {selectedProduct && (
                    <>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="产品ID">{selectedProduct.id}</Descriptions.Item>
                            <Descriptions.Item label="产品类型">
                                <Tag color="blue">{selectedProduct.product_type_code?.toUpperCase()}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="产品名称">{selectedProduct.name}</Descriptions.Item>
                            <Descriptions.Item label="Slug">{selectedProduct.slug}</Descriptions.Item>
                            <Descriptions.Item label="计费类型">
                                <Tag color="purple">
                                    {selectedProduct.billing_type === 'recurring' ? '周期性' :
                                     selectedProduct.billing_type === 'onetime' ? '一次性' :
                                     selectedProduct.billing_type === 'metered' ? '按量计费' :
                                     selectedProduct.billing_type}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="显示顺序">{selectedProduct.display_order}</Descriptions.Item>
                            <Descriptions.Item label="状态" span={2}>
                                <Tag color={selectedProduct.is_active ? 'success' : 'default'}>
                                    {selectedProduct.is_active ? '启用' : '禁用'}
                                </Tag>
                            </Descriptions.Item>
                            {selectedProduct.cdn_group_id && (
                                <Descriptions.Item label="CDN分组ID" span={2}>
                                    {selectedProduct.cdn_group_id}
                                </Descriptions.Item>
                            )}
                            {selectedProduct.dns_parent_group_ids && selectedProduct.dns_parent_group_ids.length > 0 && (
                                <Descriptions.Item label="DNS父节点组" span={2}>
                                    {Array.isArray(selectedProduct.dns_parent_group_ids)
                                        ? selectedProduct.dns_parent_group_ids.join(', ')
                                        : selectedProduct.dns_parent_group_ids}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="产品描述" span={2}>
                                {selectedProduct.description || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider>订阅统计</Divider>
                        <Descriptions bordered column={3}>
                            <Descriptions.Item label="总订阅数">
                                {selectedProduct.subscription_stats?.total || 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="活跃订阅">
                                {selectedProduct.subscription_stats?.active || 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="待激活">
                                {selectedProduct.subscription_stats?.pending || 0}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider>订阅级别限制</Divider>
                        <Descriptions bordered column={2} size="small">
                            {(() => {
                                let subscriptionLimits = selectedProduct.subscription_limits;
                                if (typeof subscriptionLimits === 'string') {
                                    try {
                                        subscriptionLimits = JSON.parse(subscriptionLimits);
                                    } catch (e) {
                                        subscriptionLimits = {};
                                    }
                                }
                                if (subscriptionLimits && typeof subscriptionLimits === 'object' && Object.keys(subscriptionLimits).length > 0) {
                                    return Object.entries(subscriptionLimits).map(([key, value]) => (
                                        <Descriptions.Item
                                            key={key}
                                            label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        >
                                            {typeof value === 'boolean' ? (
                                                <Tag color={value ? 'green' : 'red'}>{value ? '是' : '否'}</Tag>
                                            ) : (
                                                String(value)
                                            )}
                                        </Descriptions.Item>
                                    ));
                                } else {
                                    return (
                                        <Descriptions.Item label="配置" span={2}>
                                            <Text type="secondary">无配置</Text>
                                        </Descriptions.Item>
                                    );
                                }
                            })()}
                        </Descriptions>

                        <Divider>周期性使用量配额</Divider>
                        <Descriptions bordered column={2} size="small">
                            {(() => {
                                let periodicUsage = selectedProduct.periodic_usage;
                                if (typeof periodicUsage === 'string') {
                                    try {
                                        periodicUsage = JSON.parse(periodicUsage);
                                    } catch (e) {
                                        periodicUsage = {};
                                    }
                                }
                                const resetCycleLabels = {
                                    daily: '每日',
                                    weekly: '每周',
                                    monthly: '每月',
                                    quarterly: '每季度',
                                    yearly: '每年'
                                };
                                if (periodicUsage && typeof periodicUsage === 'object' && Object.keys(periodicUsage).length > 0) {
                                    return Object.entries(periodicUsage).map(([key, val]) => (
                                        <Descriptions.Item
                                            key={key}
                                            label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        >
                                            {String(val?.value ?? val)}
                                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                                {resetCycleLabels[val?.reset_cycle] || val?.reset_cycle || 'monthly'}重置
                                            </Tag>
                                        </Descriptions.Item>
                                    ));
                                } else {
                                    return (
                                        <Descriptions.Item label="配置" span={2}>
                                            <Text type="secondary">无配置</Text>
                                        </Descriptions.Item>
                                    );
                                }
                            })()}
                        </Descriptions>

                        <Divider>资源级配额特性</Divider>
                        <Descriptions bordered column={2} size="small">
                            {(() => {
                                let resourceFeatures = selectedProduct.resource_features;
                                if (typeof resourceFeatures === 'string') {
                                    try {
                                        resourceFeatures = JSON.parse(resourceFeatures);
                                    } catch (e) {
                                        resourceFeatures = {};
                                    }
                                }
                                if (resourceFeatures && typeof resourceFeatures === 'object' && Object.keys(resourceFeatures).length > 0) {
                                    return Object.entries(resourceFeatures).map(([key, value]) => (
                                        <Descriptions.Item
                                            key={key}
                                            label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        >
                                            {typeof value === 'boolean' ? (
                                                <Tag color={value ? 'green' : 'red'}>{value ? '是' : '否'}</Tag>
                                            ) : (
                                                String(value)
                                            )}
                                        </Descriptions.Item>
                                    ));
                                } else {
                                    return (
                                        <Descriptions.Item label="配置" span={2}>
                                            <Text type="secondary">无配置</Text>
                                        </Descriptions.Item>
                                    );
                                }
                            })()}
                        </Descriptions>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default Products;
